/** @jsxRuntime classic */
/** @jsx jsx */
import { useEffect, useState } from 'react';
import { css, jsx } from '@emotion/react';
import { useLocation } from 'react-router-dom';

import { Helmet } from 'react-helmet-async';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import FilterVintageOutlinedIcon from '@mui/icons-material/FilterVintageOutlined';

import Aside from './Aside';
import { MEETING_ASIDE_OPEN, MINIMIZED_MENU, useStore } from '../store';

import { useUserQuery } from '../actions/UserActions';
import { track } from '../utils/track';
import { useIsMobile } from '../utils/hooks';

import PublicHeader from './PublicHeader';
import TOSDialog from './TOSDialog';
import SkeletonContext from '../contexts/SkeletonContext';

export default function Skeleton({
  title = 'Klang.ai',
  children,
  publicPage = false,
  guestPage = false,
  rootCss,
  maxWidth = 'var(--max-content-width)',
  fullWidth = true,
  overflowHidden = false,
  addPage = true,
  showLanguageSelector = true,
  showSignupLinks = false,
  showLoginLinks = false,
  indentPage = false,
  addMaxWidth = false,
  noBackground = false,
  fetchUser = true,
  backgroundColor = null,
}) {
  const location = useLocation();
  const { isMobile, isTablet } = useIsMobile();
  const [hideMenu, setHideMenu] = useState(isMobile);
  const [minimizedMenu, setMinimizedMenu] = useStore(MINIMIZED_MENU);
  const { user } = useUserQuery({ enabled: fetchUser });
  //const isGuest = !user || !user.role || user.role === 'guest';

  useEffect(() => {
    track('pageview', {
      page: location.pathname,
    });
  }, [location]);

  useEffect(() => {
    if (isMobile) {
      setHideMenu(true);
    }
  }, [isMobile]);

  useEffect(() => {
    if (isTablet && !isMobile) {
      setMinimizedMenu(true);
    }
  }, [isTablet, isMobile, setMinimizedMenu]);

  if (!rootCss) {
    if (publicPage) {
      rootCss = css`
        background: var(--background-white);
        width: 100%;
        display: ${publicPage ? 'block' : 'flex'};
        flex-direction: column;
      `;
    } else {
      rootCss = css`
        background: var(--background);
        width: 100%;
        display: ${publicPage ? 'block' : 'flex'};
        flex-direction: column;
      `;
    }
  }

  //const isTranscript = location.pathname.includes('/transcript');

  return (
    <SkeletonContext.Provider value={{ guestPage, publicPage, fetchUser }}>
      <div css={rootCss}>
        <Helmet>
          <title>{title}</title>
        </Helmet>
        {!publicPage && !guestPage ? <TOSDialog /> : null}
        {publicPage ? (
          <PublicHeader
            guestPage={guestPage}
            isTablet={isTablet}
            isMobile={isMobile}
            showLanguageSelector={showLanguageSelector}
            showSignupLinks={showSignupLinks}
            showLoginLinks={showLoginLinks}
          />
        ) : null}
        <div
          css={css`
            display: flex;
            gap: 8px;
            height: ${publicPage
              ? 'inherit'
              : 'calc(100dvh - var(--header-height))'};
            background: var(--background);
            padding: ${isMobile || publicPage ? '0' : '0 8px'};
            margin-top: ${isMobile ? '0' : '8px'};
          `}
        >
          {!publicPage && isMobile ? (
            <IconButton
              onClick={() => setHideMenu(!hideMenu)}
              css={css`
                position: absolute;
                left: 4px;
                top: 4px;
                background: #fff;
                z-index: 999;
              `}
            >
              <MenuIcon />
            </IconButton>
          ) : null}
          {!publicPage ? (
            <Aside
              isMobile={isMobile}
              isTablet={isTablet}
              hideMenu={hideMenu}
              setHideMenu={setHideMenu}
              minimizedMenu={minimizedMenu}
              guestPage={guestPage}
            />
          ) : null}
          <div
            id="overflowdiv"
            css={css`
              display: flex;
              gap: 8px;
              margin-right: auto;
              margin-left: auto;
              max-width: ${publicPage || isMobile
                ? '100vw'
                : 'calc(100vw - 16px)'};
              width: ${fullWidth ? '100%' : 'auto'};
              overflow-x: hidden;
              background: var(--background);
              height: ${publicPage
                ? 'inherit'
                : 'calc(100dvh - var(--header-height))'};
              ${overflowHidden || publicPage ? 'overflow: hidden;' : ''}
            `}
          >
            {addPage ? (
              <div
                css={css`
                  ${!noBackground
                    ? `background: ${
                        backgroundColor || 'var(--background-white)'
                      };`
                    : ''}
                  border-radius: ${isMobile || publicPage ? '0px' : '8px'};
                  height: ${isMobile
                    ? 'calc(100dvh - var(--header-height))'
                    : 'calc(100dvh - var(--header-height) - 8px)'};
                  ${publicPage ? 'height: inherit;' : ''}
                  box-sizing: border-box;
                  padding: 0 ${!isMobile && indentPage ? 4.5 : 1}rem;
                  flex-grow: 1;
                  width: 100%;
                  overflow-y: auto;
                  overflow-x: hidden;
                `}
              >
                {addMaxWidth ? (
                  <div
                    css={css`
                      max-width: 1040px;
                      margin: 0 auto;
                    `}
                  >
                    {children}
                  </div>
                ) : (
                  children
                )}
              </div>
            ) : (
              children
            )}
          </div>
        </div>
      </div>
    </SkeletonContext.Provider>
  );
}
